// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCity } from '../context/CityContext';
import { useShortlist } from '../context/ShortlistContext';
import { useAuth } from '../context/AuthContext';
import '../styles/Navbar.css';
import CityModal from './CityModal';
import BuyCarDropdown from './BuyCarDropdown';
import SellCarDropdown from './SellCarDropdown';
import MoreDropdown from './MoreDropdown';
import AccountDropdown from './AccountDropdown';
import SearchBar from './SearchBar';
import LoginModal from './LoginModal';
import logo from '../assets/caryugo1-logo.jpeg';

const Navbar = () => {
  const [showCityModal, setShowCityModal]   = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen, setMobileOpen]         = useState(false);
  const [mobileTab, setMobileTab]           = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { selectedCity, setSelectedCity } = useCity();
  const { shortlistCount }                = useShortlist();
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const closeTimerRef  = useRef(null);
  const cityTimerRef   = useRef(null);
  const cityWrapperRef = useRef(null);

  useEffect(() => { setMobileOpen(false); setMobileTab(null); }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  useEffect(() => () => {
    clearTimeout(closeTimerRef.current);
    clearTimeout(cityTimerRef.current);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (!e.target.closest('.nav-dropdown-wrapper')) setActiveDropdown(null);
      if (cityWrapperRef.current && !cityWrapperRef.current.contains(e.target)) setShowCityModal(false);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  const handleCitySelect = (city) => {
    setSelectedCity(city);
    setShowCityModal(false);
    setMobileOpen(false);
    setMobileTab(null);
    if (location.pathname === '/buy-cars') {
      const p = new URLSearchParams(location.search);
      p.set('location', city);
      navigate(`/buy-cars?${p.toString()}`);
    } else if (location.pathname.startsWith('/services')) {
      navigate(`/services/${city.toLowerCase().replace(/\s+/g, '-')}`);
    }
  };

  /* desktop hover */
  const handleMouseEnter = (key) => { clearTimeout(closeTimerRef.current); setActiveDropdown(key); };
  const handleMouseLeave = () => { closeTimerRef.current = setTimeout(() => setActiveDropdown(null), 150); };
  const toggleDropdown   = (key) => setActiveDropdown(p => p === key ? null : key);
  const handleCityEnter  = () => { clearTimeout(cityTimerRef.current); setShowCityModal(true); };
  const handleCityLeave  = () => { cityTimerRef.current = setTimeout(() => setShowCityModal(false), 400); };

  const renderDropdown = (key, label, Component) => (
    <div className="nav-dropdown-wrapper" onMouseEnter={() => handleMouseEnter(key)} onMouseLeave={handleMouseLeave}>
      <button className={`nav-dropdown-button ${activeDropdown === key ? 'active' : ''}`} onClick={() => toggleDropdown(key)}>
        {label} <span className="dropdown-arrow">▼</span>
      </button>
      {activeDropdown === key && <Component onClose={() => setActiveDropdown(null)} />}
    </div>
  );

  const toggleTab = (tab) => setMobileTab(p => p === tab ? null : tab);

  const getUserLabel = () => {
    if (!user) return 'Account';
    if (user.phone) return `+91 ${user.phone}`;
    if (user.name)  return user.name;
    if (user.email && !user.email.includes('temp.')) return user.email;
    return 'My Account';
  };

  const handleMobileLogout = async () => { await logout(); setMobileOpen(false); navigate('/'); };

  const buyCities = [
    { label: 'View All Cars',          path: '/buy-cars' },
    { label: 'Used cars in Indore',    path: '/buy-cars?location=Indore',    city: 'Indore' },
    { label: 'Used cars in Mhow',      path: '/buy-cars?location=Mhow',      city: 'Mhow' },
    { label: 'Used cars in Pithampur', path: '/buy-cars?location=Pithampur', city: 'Pithampur' },
    { label: 'Used cars in Dewas',     path: '/buy-cars?location=Dewas',     city: 'Dewas' },
    { label: 'Used cars in Ujjain',    path: '/buy-cars?location=Ujjain',    city: 'Ujjain' },
  ];
  const sellCities = [
    { label: 'Sell Car Online',        path: '/sell-car/indore/journey' },
    { label: 'Sell cars in Indore',    path: '/sell-car/indore/journey' },
    { label: 'Sell cars in Dewas',     path: '/sell-car/dewas/journey' },
    { label: 'Sell cars in Ujjain',    path: '/sell-car/ujjain/journey' },
    { label: 'Sell cars in Mhow',      path: '/sell-car/mhow/journey' },
    { label: 'Sell cars in Pithampur', path: '/sell-car/pithampur/journey' },
  ];
  const moreLinks = [
    { icon: '📋', label: 'About Us',               path: '/about' },
    { icon: '💰', label: 'Finance your Car',        path: '/finance' },
    { icon: '🔧', label: 'Service Car',             path: `/services/${selectedCity.toLowerCase().replace(/\s+/g,'-')}` },
    { icon: '😊', label: 'How it Works',            path: '/how-it-works' },
    { icon: '📍', label: 'Car Hub Locations',       path: '/locations' },
    { icon: '📝', label: 'Blog',                    path: '/blog' },
    { icon: '💬', label: 'Customer Reviews',        path: '/reviews' },
    { icon: '🚗', label: 'Scrap Car',               path: '/scrap-car' },
    { icon: '💵', label: 'Car Valuation',           path: '/valuation' },
    { icon: '✅', label: 'Check eChallan',          path: '/echallan' },
    { icon: '🧮', label: 'Service Cost Calculator', path: '/service-cost-calculator' },
  ];
  const mpCities = ['Indore', 'Mhow', 'Pithampur', 'Dewas', 'Ujjain'];

  return (
    <>
      {/* ══ NAVBAR BAR ══ */}
      <nav className="navbar">
        <div className="navbar-container">

          <Link to="/" className="navbar-logo">
              <img src={logo} alt="CarYugo Logo" className="logo-img" />
          </Link>

          {/* City — desktop */}
          <div ref={cityWrapperRef} className="city-selector-wrapper" onMouseEnter={handleCityEnter} onMouseLeave={handleCityLeave}>
            <button className="city-selector" onClick={() => setShowCityModal(p => !p)}>
              {selectedCity} <span className="dropdown-arrow">▼</span>
            </button>
            {showCityModal && (
              <CityModal onClose={() => setShowCityModal(false)} onSelectCity={handleCitySelect} currentCity={selectedCity} />
            )}
          </div>

          {/* Search — desktop */}
          <div className="navbar-search-wrap">
            <SearchBar />
          </div>

          {/* Desktop nav actions */}
          <div className="navbar-actions">
            {renderDropdown('buy',  'Buy car',  BuyCarDropdown)}
            {renderDropdown('sell', 'Sell car', SellCarDropdown)}
            {renderDropdown('more', 'More',     MoreDropdown)}

            <Link to="/shortlisted" className="icon-link shortlist-nav-link">
              <div className="shortlist-icon-wrapper">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="22" height="22">
                  <path d="M20.8 4.6c-1.5-1.5-4-1.5-5.5 0L12 7.9l-3.3-3.3c-1.5-1.5-4-1.5-5.5 0s-1.5 4 0 5.5l3.3 3.3L12 21l5.5-7.6 3.3-3.3c1.5-1.5 1.5-4 0-5.5z"/>
                </svg>
                {shortlistCount > 0 && <span className="shortlist-badge">{shortlistCount}</span>}
              </div>
              <span className="icon-text">Shortlisted</span>
            </Link>

            <AccountDropdown />
          </div>

          {/* Hamburger — mobile only */}
          <button
            className={`hamburger ${mobileOpen ? 'hamburger--open' : ''}`}
            onClick={() => setMobileOpen(p => !p)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* ══ MOBILE DRAWER ══ */}
      {mobileOpen && <div className="drawer-overlay" onClick={() => setMobileOpen(false)} />}

      <div className={`drawer ${mobileOpen ? 'drawer--open' : ''}`}>

        {/* Search */}
        <div className="drawer-search">
          <SearchBar />
        </div>

        {/* City */}
        <div className="drawer-item">
          <button className={`drawer-row ${mobileTab==='city'?'drawer-row--active':''}`} onClick={() => toggleTab('city')}>
            <span className="drawer-icon">📍</span>
            <span className="drawer-label">City: <b>{selectedCity}</b></span>
            <span className={`drawer-chevron ${mobileTab==='city'?'drawer-chevron--open':''}`}>▼</span>
          </button>
          {mobileTab === 'city' && (
            <div className="drawer-sub">
              <p className="drawer-sub-title">M.P — Available Now</p>
              {mpCities.map((c, i) => (
                <button key={i} className="drawer-sub-btn" onClick={() => handleCitySelect(c)}>
                  <span className="drawer-dot" />{c}
                  {selectedCity === c && <span className="drawer-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Buy Car */}
        <div className="drawer-item">
          <button className={`drawer-row ${mobileTab==='buy'?'drawer-row--active':''}`} onClick={() => toggleTab('buy')}>
            <span className="drawer-icon">🚗</span>
            <span className="drawer-label">Buy Car</span>
            <span className={`drawer-chevron ${mobileTab==='buy'?'drawer-chevron--open':''}`}>▼</span>
          </button>
          {mobileTab === 'buy' && (
            <div className="drawer-sub">
              {buyCities.map((item, i) => (
                <button key={i} className="drawer-sub-btn" onClick={() => { if(item.city) setSelectedCity(item.city); navigate(item.path); setMobileOpen(false); }}>
                  <span className="drawer-dot" />{item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sell Car */}
        <div className="drawer-item">
          <button className={`drawer-row ${mobileTab==='sell'?'drawer-row--active':''}`} onClick={() => toggleTab('sell')}>
            <span className="drawer-icon">💰</span>
            <span className="drawer-label">Sell Car</span>
            <span className={`drawer-chevron ${mobileTab==='sell'?'drawer-chevron--open':''}`}>▼</span>
          </button>
          {mobileTab === 'sell' && (
            <div className="drawer-sub">
              {sellCities.map((item, i) => (
                <button key={i} className="drawer-sub-btn" onClick={() => { navigate(item.path); setMobileOpen(false); }}>
                  <span className="drawer-dot" />{item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* More */}
        <div className="drawer-item">
          <button className={`drawer-row ${mobileTab==='more'?'drawer-row--active':''}`} onClick={() => toggleTab('more')}>
            <span className="drawer-icon">☰</span>
            <span className="drawer-label">More</span>
            <span className={`drawer-chevron ${mobileTab==='more'?'drawer-chevron--open':''}`}>▼</span>
          </button>
          {mobileTab === 'more' && (
            <div className="drawer-sub">
              {moreLinks.map((item, i) => (
                <button key={i} className="drawer-sub-btn" onClick={() => { navigate(item.path); setMobileOpen(false); }}>
                  <span className="drawer-emoji">{item.icon}</span>{item.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Shortlisted */}
        <div className="drawer-item">
          <Link className="drawer-row" to="/shortlisted" onClick={() => setMobileOpen(false)}>
            <span className="drawer-icon">♡</span>
            <span className="drawer-label">
              Shortlisted
              {shortlistCount > 0 && <span className="drawer-badge">{shortlistCount}</span>}
            </span>
            <span className="drawer-arrow-plain">›</span>
          </Link>
        </div>

        {/* Account */}
        <div className="drawer-item">
          <button className={`drawer-row ${mobileTab==='account'?'drawer-row--active':''}`} onClick={() => toggleTab('account')}>
            <span className="drawer-icon">👤</span>
            <span className="drawer-label">{getUserLabel()}</span>
            <span className={`drawer-chevron ${mobileTab==='account'?'drawer-chevron--open':''}`}>▼</span>
          </button>
          {mobileTab === 'account' && (
            <div className="drawer-sub">
              {isAuthenticated ? (
                <>
                  <button className="drawer-sub-btn" onClick={() => { navigate('/profile'); setMobileOpen(false); }}>
                    <span className="drawer-emoji">👤</span>Your Account
                  </button>
                  <button className="drawer-sub-btn" onClick={() => { navigate('/shortlisted'); setMobileOpen(false); }}>
                    <span className="drawer-emoji">♡</span>Shortlisted Cars
                  </button>
                  <button className="drawer-sub-btn" onClick={() => { navigate('/my-sells'); setMobileOpen(false); }}>
                    <span className="drawer-emoji">🚗</span>My Sells
                  </button>
                  <button className="drawer-sub-btn drawer-sub-btn--red" onClick={handleMobileLogout}>
                    <span className="drawer-emoji">🚪</span>Logout
                  </button>
                </>
              ) : (
                <>
                  <button className="drawer-sub-btn" onClick={() => { navigate('/login'); setMobileOpen(false); }}>
                    <span className="drawer-emoji">🔑</span>Login / Sign up
                  </button>
                  <button className="drawer-sub-btn" onClick={() => { setShowLoginModal(true); setMobileOpen(false); }}>
                    <span className="drawer-emoji">📱</span>Login with OTP
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* CTA when logged out */}
        {!isAuthenticated && (
          <div className="drawer-cta">
            <button className="drawer-cta-btn" onClick={() => { navigate('/login'); setMobileOpen(false); }}>
              Login / Sign up
            </button>
          </div>
        )}

      </div>

      {showLoginModal && <LoginModal onClose={() => setShowLoginModal(false)} />}
    </>
  );
};

export default Navbar;